import React, { useState, useEffect, Suspense, useRef, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { 
  FileText, Upload, 
  History, GraduationCap,
  WifiOff,
  X, Sliders,
  ScanLine, ArrowUp,
  Share2, Check, Mail
} from "lucide-react";

import { DetectionResult, HistoryItem, UserFeedback, DomainPreset } from "./types";
import { 
  analyzeTextLocally, 
  recomputePredictionWithThresholds,
  clearAnalysisCache,
  DEFAULT_CLIENT_WEIGHTS, 
  DOMAIN_PRESET_WEIGHTS, 
  ModelWeights, 
  TrainingRow,
  TrainingSummaryData,
  getTypingPreviewMetrics
} from "./lib/detectClient";
import { sanitizeUserInput, detectAndSanitizePromptInjection } from "./lib/sanitize";
import { formatUserFacingError, countWords } from "./lib/validation";
import { getUserSessionId, filterOwnedData } from "./lib/authSession";
import { safeLocalStorage, safeSessionStorage, safeRandomUUID, subscribeMediaQuery } from "./lib/safeStorage";
import { extractClipboardFile } from "./lib/documentExtractor";
import { checkDailyComputeLimit, recordDailyComputeUsage, estimateBase64SizeBytes } from "./lib/ocrRateLimiter";
import { ResultSkeletonLoader, HistorySkeletonLoader, OcrSkeletonLoader } from "./components/SkeletonLoader";
import ToastContainer, { ToastMessage, ToastType } from "./components/Toast";
import { TextEditorPanel } from "./components/TextEditorPanel";
import ThemeSelector, { ThemeMode } from "./components/ThemeSelector";
import { CookieBanner } from "./components/CookieBanner";
import { executeResilientDetection } from "./lib/resilience";
import { shareWebsite, getWebsiteAddress } from "./lib/shareUtils";
import { CatchAiLogo } from "./components/CatchAiLogo";

import { lazyWithRetry } from "./lib/lazyWithRetry";

// Core results and secondary workspaces lazy-loaded with automatic retry resilience
const DetectionResultsSection = lazyWithRetry(() => import("./components/DetectionResultsSection"));
const OcrPreprocessor = lazyWithRetry(() => import("./components/OcrPreprocessor"));
const HistoryPanel = lazyWithRetry(() => import("./components/HistoryPanel"));
const ModelCalibrationModal = lazyWithRetry(() => import("./components/ModelCalibrationModal"));
const AdminPortalLogin = lazyWithRetry(() => import("./components/AdminPortalLogin"));
const ScoreInterpretationModal = lazyWithRetry(() => import("./components/ScoreInterpretationModal").then(m => ({ default: m.ScoreInterpretationModal })));
import AcademicInfoSection from "./components/AcademicInfoSection";
import { LegalHubPage } from "./components/LegalHubPage";
import type { LegalSectionId } from "./components/LegalHubPage";

const VALID_LEGAL_SECTIONS: LegalSectionId[] = ["terms", "privacy", "ethics", "about", "dmca", "score-guide"];

const LEGAL_SECTION_SEO: Record<LegalSectionId, { title: string; description: string }> = {
  about: {
    title: "About & Methodology | CatchAI Text Stylometric Forensics",
    description:
      "Learn how CatchAI Text detects AI writing using 10 mathematical stylometric signals including sentence burstiness, Shannon entropy, Zipf's Law, and Herdan's C lexical diversity."
  },
  "score-guide": {
    title: "How to Interpret AI Detection Scores | CatchAI Text Guide",
    description:
      "Understand CatchAI Text probability bands (0–30% Human, 31–69% Borderline/Mixed, 70–100% Likely AI), false-positive guardrails, and fair review protocols for educators."
  },
  ethics: {
    title: "AI Ethics, EU AI Act Compliance & ESL Fairness | CatchAI Text",
    description:
      "Read CatchAI Text's ethical governance framework: EU AI Act (Regulation 2024/1689) human-in-the-loop compliance, non-native English (ESL) bias mitigation, and student rights."
  },
  privacy: {
    title: "Privacy Policy (GDPR, FERPA & Zero Retention) | CatchAI Text",
    description:
      "CatchAI Text Privacy Policy: 100% client-side browser text analysis, zero document retention, FERPA & GDPR student data protection, and self-hosted fonts."
  },
  terms: {
    title: "Terms of Service & Acceptable Use Policy | CatchAI Text",
    description:
      "Read the CatchAI Text Terms of Service governing academic, editorial, and API usage, mandatory human-in-the-loop verification rules, and warranty disclaimers."
  },
  dmca: {
    title: "DMCA Policy & Copyright Notice Procedure | CatchAI Text",
    description:
      "Official Digital Millennium Copyright Act (17 U.S.C. § 512) Safe Harbor policy, zero user-content hosting architecture, and designated copyright agent contact for CatchAI Text."
  }
};

const DEFAULT_HOME_TITLE = "CatchAI Text | AI Writing Analysis & Authorship Verification";
const DEFAULT_HOME_DESCRIPTION =
  "Analyze text for AI generation with CatchAI Text. Free, 100% client-side stylometric detector measuring burstiness, entropy, and Zipf's law with Word, PDF, and OCR support.";

function resolveLegalSectionFromUrl(): LegalSectionId | null {
  if (typeof window === "undefined") return null;
  const pathClean = window.location.pathname.replace(/^\/+|\/+$/g, "").toLowerCase();
  if (VALID_LEGAL_SECTIONS.includes(pathClean as LegalSectionId)) {
    return pathClean as LegalSectionId;
  }
  const hashClean = window.location.hash.replace(/^#+/, "").toLowerCase();
  if (VALID_LEGAL_SECTIONS.includes(hashClean as LegalSectionId)) {
    return hashClean as LegalSectionId;
  }
  return null;
}

function getCustomDefaultWeights(): ModelWeights | null {
  try {
    const raw =
      safeLocalStorage.getItem("catchai_custom_default_weights_v1") ||
      safeLocalStorage.getItem("veritext_custom_default_weights_v1");
    if (!raw) return null;
    return JSON.parse(raw) as ModelWeights;
  } catch {
    return null;
  }
}

export default function App() {
  // Main Workspace State
  const [activeTab, setActiveTab] = useState<"paste" | "ocr">("paste");
  const [domainPreset, setDomainPreset] = useState<DomainPreset>("general");

  // Model calibration state (checks session override, then persistent custom app default, then factory baseline)
  const [weights, setWeights] = useState<ModelWeights>(() => {
    try {
      const isCalibrated =
        safeSessionStorage.getItem("catchai_calibrated_v4") ||
        safeSessionStorage.getItem("veritext_calibrated_v4");
      const saved =
        safeSessionStorage.getItem("catchai_model_weights") ||
        safeSessionStorage.getItem("veritext_model_weights");
      if (saved && isCalibrated) {
        return JSON.parse(saved);
      }
      const customDefaultWeights = getCustomDefaultWeights();
      if (customDefaultWeights && isCalibrated) {
        return customDefaultWeights;
      }
      safeSessionStorage.setItem("catchai_calibrated_v4", "true");
      safeSessionStorage.setItem("catchai_model_weights", JSON.stringify(DEFAULT_CLIENT_WEIGHTS));
    } catch {}
    return { ...DEFAULT_CLIENT_WEIGHTS };
  });
  const [isCalibrationModalOpen, setIsCalibrationModalOpen] = useState<boolean>(false);
  const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState<boolean>(false);
  const [isScoreGuideOpen, setIsScoreGuideOpen] = useState<boolean>(false);

  // Dedicated multi-page view state (Supports both clean /about, /privacy, /terms paths and #hash fallback)
  const [currentView, setCurrentView] = useState<"detector" | "legal">(() => {
    return resolveLegalSectionFromUrl() ? "legal" : "detector";
  });
  const [activeLegalSection, setActiveLegalSection] = useState<LegalSectionId>(() => {
    return resolveLegalSectionFromUrl() || "privacy";
  });

  // Synchronize document title, canonical URL, meta description, and OpenGraph tags with active view for Googlebot & SEO
  useEffect(() => {
    const isLegal = currentView === "legal";
    const seoEntry = isLegal ? LEGAL_SECTION_SEO[activeLegalSection] : null;
    const nextTitle = seoEntry?.title || DEFAULT_HOME_TITLE;
    const nextDescription = seoEntry?.description || DEFAULT_HOME_DESCRIPTION;
    const canonicalPath = isLegal ? `/${activeLegalSection}` : "/";
    const canonicalHref = `https://catchaitext.com${canonicalPath}`;

    document.title = nextTitle;

    const setMetaContent = (selector: string, content: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute("content", content);
    };

    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute("href", canonicalHref);
    }

    setMetaContent('meta[name="title"]', nextTitle);
    setMetaContent('meta[name="description"]', nextDescription);
    setMetaContent('meta[property="og:title"]', nextTitle);
    setMetaContent('meta[property="og:description"]', nextDescription);
    setMetaContent('meta[property="og:url"]', canonicalHref);
    setMetaContent('meta[name="twitter:title"]', nextTitle);
    setMetaContent('meta[name="twitter:description"]', nextDescription);
    setMetaContent('meta[name="twitter:url"]', canonicalHref);
  }, [currentView, activeLegalSection]);

  // Dedicated multi-page style navigation (Clean pathname URLs matching sitemap.xml)
  const handleNavigateToLegal = useCallback((section: LegalSectionId = "privacy") => {
    setActiveLegalSection(section);
    setCurrentView("legal");
    try {
      window.history.pushState({ view: "legal", section }, "", `/${section}`);
    } catch {}
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleNavigateToHome = useCallback(() => {
    setCurrentView("detector");
    try {
      if (window.location.pathname !== "/" || window.location.hash) {
        window.history.pushState({ view: "detector" }, "", "/");
      }
    } catch {}
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Listen to browser Back/Forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const resolved = resolveLegalSectionFromUrl();
      if (resolved) {
        setActiveLegalSection(resolved);
        setCurrentView("legal");
      } else {
        setCurrentView("detector");
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  // Non-obvious Admin Portal & Server Session State
  const [currentPath, setCurrentPath] = useState<string>(() => window.location.pathname);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState<boolean>(false);

  // Synchronize latest active model weights from server (e.g. after background retraining or admin adjustments)
  useEffect(() => {
    const hasLocalOverride =
      safeSessionStorage.getItem("catchai_user_custom_weights") ||
      safeSessionStorage.getItem("veritext_user_custom_weights");
    if (!hasLocalOverride) {
      fetch("/api/weights")
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data?.weights && typeof data.weights === "object") {
            setWeights(data.weights);
            safeSessionStorage.setItem("catchai_model_weights", JSON.stringify(data.weights));
            safeSessionStorage.setItem("veritext_model_weights", JSON.stringify(data.weights));
          }
        })
        .catch(() => {});
    }
  }, []);

  // Helper to open legal compliance terms on dedicated full page (Claude/Amazon pattern)
  const handleOpenPrivacyPolicy = useCallback(() => {
    handleNavigateToLegal("privacy");
  }, [handleNavigateToLegal]);

  // Helper to close calibration modal and strip any ?admin=... query parameter from the address bar
  const handleCloseCalibrationModal = useCallback(() => {
    setIsCalibrationModalOpen(false);
    if (window.location.search.includes("admin")) {
      const url = new URL(window.location.href);
      url.searchParams.delete("admin");
      window.history.replaceState({}, "", url.pathname + (url.search ? url.search : "") + url.hash);
    }
  }, []);

  // Global Esc key handler: closes any open modal, drawer, or dialog
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (isHistoryDrawerOpen) {
          setIsHistoryDrawerOpen(false);
          return;
        }
        if (isCalibrationModalOpen) {
          handleCloseCalibrationModal();
          return;
        }
      }
    };
    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [isHistoryDrawerOpen, isCalibrationModalOpen, handleCloseCalibrationModal]);

  // Check server admin session and listen for path changes
  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };
    window.addEventListener("popstate", handleLocationChange);

    // Initial check of server-side admin authentication session
    fetch("/api/admin/session", { credentials: "include" })
      .then((res) => {
        if (res.ok) {
          setIsAdminAuthenticated(true);
          const searchParams = new URLSearchParams(window.location.search);
          if (searchParams.get("admin") === "true") {
            setIsCalibrationModalOpen(true);
            // Immediately clean up the query parameter from address bar
            const url = new URL(window.location.href);
            url.searchParams.delete("admin");
            window.history.replaceState({}, "", url.pathname + (url.search ? url.search : "") + url.hash);
          }
        } else {
          // If not authenticated, ensure no lingering ?admin query in address bar
          if (window.location.search.includes("admin")) {
            const url = new URL(window.location.href);
            url.searchParams.delete("admin");
            window.history.replaceState({}, "", url.pathname + (url.search ? url.search : "") + url.hash);
          }
        }
      })
      .catch(() => {});

    // Purge legacy passcode from local client storage
    try {
      window.sessionStorage.removeItem("catchai_admin_passcode");
      window.localStorage.removeItem("catchai_admin_passcode");
      window.sessionStorage.removeItem("veritext_admin_passcode");
      window.localStorage.removeItem("veritext_admin_passcode");
    } catch {}

    return () => {
      window.removeEventListener("popstate", handleLocationChange);
    };
  }, []);

  useEffect(() => {
    (window as any).__catchai_mounted = true;
    (window as any).__veritext_mounted = true;
  }, []);

  // Scroll-direction show/hide behavior for top navigation bar
  const [isNavVisible, setIsNavVisible] = useState<boolean>(true);

  useEffect(() => {
    let lastScrollY = window.scrollY || window.pageYOffset || 0;
    let ticking = false;
    let isVisible = true;
    const SCROLL_THRESHOLD = 10;
    const TOP_OFFSET_LIMIT = 100;

    const getScrollPosition = () => {
      return Math.max(0, window.scrollY || window.pageYOffset || 0);
    };

    const updateNavVisibility = () => {
      const currentScrollY = getScrollPosition();

      // No hiding while at the very top of the page (first ~100px of scroll)
      if (currentScrollY <= TOP_OFFSET_LIMIT) {
        if (!isVisible) {
          isVisible = true;
          setIsNavVisible(true);
        }
        lastScrollY = currentScrollY;
      } else {
        const diff = currentScrollY - lastScrollY;
        // Filter out micro-jitter with threshold (~10px)
        if (Math.abs(diff) >= SCROLL_THRESHOLD) {
          if (diff > 0) {
            // User scrolls DOWN -> Nav bar stays visible / reappears
            if (!isVisible) {
              isVisible = true;
              setIsNavVisible(true);
            }
          } else {
            // User scrolls UP -> Nav bar hides (slides up out of view)
            if (isVisible) {
              isVisible = false;
              setIsNavVisible(false);
            }
          }
          lastScrollY = currentScrollY;
        }
      }
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateNavVisibility);
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // Model Calibration State
  const [isTraining, setIsTraining] = useState<boolean>(false);
  const [trainingSummary, setTrainingSummary] = useState<TrainingSummaryData | null>(null);

  // Detection Input State
  const [inputText, setInputText] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const activeScanIdRef = useRef<number>(0);
  const isScanningRef = useRef<boolean>(false);
  const [ocrLoading, setOcrLoading] = useState<boolean>(false);
  const [isTranscribingPastedImage, setIsTranscribingPastedImage] = useState<boolean>(false);
  const [isClearing, setIsClearing] = useState<boolean>(false);
  const [loadingSample, setLoadingSample] = useState<"academic" | "ai" | null>(null);
  const [result, setResult] = useState<DetectionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Dynamic Confidence Thresholds for real-time recalculation without API call
  const lowConfidence = 0.3;
  const highConfidence = 0.7;

  // Derived effective result that dynamically re-renders when thresholds change
  const effectiveResult = useMemo(() => {
    if (!result) return null;
    return recomputePredictionWithThresholds(result, lowConfidence, highConfidence);
  }, [result, lowConfidence, highConfidence]);

  // Theme
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    try {
      const saved =
        safeLocalStorage.getItem("catchai_theme_mode") ||
        safeLocalStorage.getItem("veritext_theme_mode");
      if (saved === "light" || saved === "dark" || saved === "system") {
        return saved;
      }
    } catch {
      // fallback to system
    }
    return "system";
  });
  const [theme, setTheme] = useState<"light" | "dark">("light");

  // History state
  const [history, setHistory] = useState<HistoryItem[]>([]);

  const [isGeneratingPdf, setIsGeneratingPdf] = useState<boolean>(false);

  // Toast notifications state
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const addToast = (
    type: ToastType,
    title: string,
    message?: string,
    duration = 2400,
    action?: { label: string; onClick: () => void }
  ) => {
    setToasts((prev) => {
      // Deduplicate identical active notifications
      const existingIdx = prev.findIndex(
        (t) => t.type === type && t.title === title && t.message === message
      );

      if (existingIdx !== -1) {
        const next = [...prev];
        const existing = next[existingIdx];
        const currentCount = existing.count || 1;
        // Bump timestamp ID to reset progress bar and timer, increment count
        next[existingIdx] = {
          ...existing,
          id: "toast-" + Date.now() + "-" + Math.random().toString(36).slice(2, 7),
          count: currentCount + 1,
          duration,
          action: action || existing.action
        };
        return next;
      }

      const id = "toast-" + Date.now() + "-" + Math.random().toString(36).slice(2, 7);
      return [...prev.slice(-1), { id, type, title, message, duration, count: 1, action }];
    });
  };

  const dismissToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const dismissAllToasts = () => {
    setToasts([]);
  };

  const [isShareCopied, setIsShareCopied] = useState<boolean>(false);

  // Optimized Share Website Link Handler (Always shares the actual public address of the website)
  const handleShareApp = useCallback(async () => {
    try {
      const res = await shareWebsite({
        title: "CatchAI Text — Stylometric Text Analysis & Authorship Engine",
        text: "Open-access AI writing forensics and document authenticity verification platform.",
        onSuccess: (url) => {
          setIsShareCopied(true);
          addToast("success", "Website Address Copied", `Link copied: ${url}`);
          setTimeout(() => setIsShareCopied(false), 2200);
        },
        onError: () => {
          addToast("error", "Share Failed", "Could not copy website address to clipboard.");
        }
      });
      if (res.copied) {
        setIsShareCopied(true);
        addToast("success", "Website Address Copied", `Link copied: ${res.url}`);
        setTimeout(() => setIsShareCopied(false), 2200);
      }
    } catch {
      addToast("error", "Share Failed", "Could not copy website address to clipboard.");
    }
  }, []);

  // Window-level Drag & Drop Active Overlay State
  const [isWindowDragging, setIsWindowDragging] = useState<boolean>(false);
  const dragCounterRef = useRef<number>(0);

  useEffect(() => {
    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer && e.dataTransfer.types.includes("Files")) {
        dragCounterRef.current += 1;
        setIsWindowDragging(true);
      }
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
      if (dragCounterRef.current === 0) {
        setIsWindowDragging(false);
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current = 0;
      setIsWindowDragging(false);
      const file = e.dataTransfer?.files?.[0];
      if (file) {
        if (file.type.startsWith("image/") || file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")) {
          setActiveTab("ocr");
          handleProcessPastedImageFile(file);
        } else {
          const reader = new FileReader();
          reader.onload = (evt) => {
            if (typeof evt.target?.result === "string") {
              setInputText(evt.target.result);
              setActiveTab("paste");
              addToast("success", "File Imported", `Loaded text from ${file.name}`);
            }
          };
          reader.readAsText(file);
        }
      }
    };

    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("drop", handleDrop);

    return () => {
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("drop", handleDrop);
    };
  }, []);

  // Offline status tracking
  const [isOffline, setIsOffline] = useState<boolean>(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Keyboard shortcut listener: Cmd/Ctrl + H opens Scan History Drawer
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "h") {
        e.preventDefault();
        setIsHistoryDrawerOpen(prev => !prev);
      }
      if (e.key === "Escape") {
        setIsHistoryDrawerOpen(false);
        handleCloseCalibrationModal();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Load history from safe storage on initial render
  useEffect(() => {
    const cached =
      safeLocalStorage.getItem("catchai_history_v2") ||
      safeLocalStorage.getItem("veritext_history_v2");
    if (cached) {
      try {
        const parsed: HistoryItem[] = JSON.parse(cached);
        const ownedItems = filterOwnedData(parsed);
        setHistory(ownedItems);
        // Migrate to catchai_history_v2
        safeLocalStorage.setItem("catchai_history_v2", cached);
      } catch (err) {
        console.error("Failed to parse cached history:", err);
      }
    }

    const savedThemeMode = (
      safeLocalStorage.getItem("catchai_theme_mode") ||
      safeLocalStorage.getItem("veritext_theme_mode")
    ) as "light" | "dark" | "system" | null;
    if (savedThemeMode) setThemeMode(savedThemeMode);
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeMediaQuery("(prefers-color-scheme: dark)", (isDark) => {
      const resolved = themeMode === "system" ? (isDark ? "dark" : "light") : themeMode;
      setTheme(resolved);
      if (resolved === "dark") {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
      safeLocalStorage.setItem("catchai_theme_mode", themeMode);
      safeLocalStorage.setItem("veritext_theme_mode", themeMode);
    });

    return () => unsubscribe();
  }, [themeMode]);

  const saveHistory = (updatedOrUpdater: HistoryItem[] | ((prev: HistoryItem[]) => HistoryItem[])) => {
    setHistory(prev => {
      const updated = typeof updatedOrUpdater === "function" ? updatedOrUpdater(prev) : updatedOrUpdater;
      try {
        const serialized = JSON.stringify(updated);
        safeLocalStorage.setItem("catchai_history_v2", serialized);
        safeLocalStorage.setItem("veritext_history_v2", serialized);
      } catch (err) {
        console.warn("Could not save history to storage:", err);
      }
      return updated;
    });
  };

  const wordCount = countWords(inputText);

  // Real-time estimated humanity calculation (statistical burstiness & Herdan's C richness)
  const estimateRealtimeHumanity = (text: string) => {
    return getTypingPreviewMetrics(text);
  };

  // Core scan execution pipeline
  const executeScan = async (textToScan: string, options?: { showToast?: boolean }) => {
    const cleanInput = detectAndSanitizePromptInjection(textToScan).sanitizedText;
    if (cleanInput !== inputText) setInputText(cleanInput);

    if (countWords(cleanInput) < 15) {
      const err = "Forensic linguistic analysis requires a minimum of 15 words to evaluate stylistic variety and burstiness patterns reliably.";
      setError(err);
      addToast("warning", "Insufficient Length", "Minimum 15 words required for forensic analysis.");
      return;
    }

    // Set synchronous lock & increment scan identifier to invalidate previous in-flight scans
    isScanningRef.current = true;
    const currentScanId = ++activeScanIdRef.current;

    setIsLoading(true);
    setError(null);
    setResult(null);

    setTimeout(() => {
      document.getElementById("results-anchor")?.scrollIntoView({ behavior: "smooth" });
    }, 40);

    try {
      await new Promise(resolve => setTimeout(resolve, 400));

      // Guard: Check if this scan has been superseded or reset while waiting
      if (activeScanIdRef.current !== currentScanId) {
        return;
      }

      // Execute with Layer 9 zero-downtime resilience (server deduplication + automatic client fallback)
      const resilientOutcome = await executeResilientDetection(cleanInput, weights, {
        lowConfidence,
        highConfidence,
        domainPreset
      });
      const detectionResult = resilientOutcome.result;

      // Guard: Check again in case state was invalidated during computation
      if (activeScanIdRef.current !== currentScanId) {
        return;
      }

      // Synchronously set results atomically
      setResult(detectionResult);

      // Safely persist to history using functional state to prevent stale-closure races
      try {
        const newItem: HistoryItem = {
          id: safeRandomUUID(),
          ownerId: getUserSessionId(),
          inputType: "paste",
          timestamp: new Date().toISOString(),
          text: cleanInput,
          result: detectionResult
        };
        saveHistory(prev => [newItem, ...prev]);
      } catch (storageErr) {
        console.warn("Could not save scan history:", storageErr);
      }

      if (options?.showToast !== false) {
        let toastTitle = detectionResult.isBorderline ? "Borderline Scan Completed" : "Linguistic Scan Complete";
        let toastMsg = `Verdict: ${detectionResult.overall_prediction} (${Math.round(detectionResult.confidence_score)}% confidence)`;
        if (resilientOutcome.source === "server-cached") {
          toastTitle = "Scan Complete (Layer 9 Cache HIT)";
          toastMsg = `Instant <1ms retrieval & 0 CPU burned. Verdict: ${detectionResult.overall_prediction}`;
        }

        addToast(
          detectionResult.isBorderline || detectionResult.overall_prediction === "Needs Human Review" ? "warning" : "success", 
          toastTitle, 
          toastMsg
        );
      }

      setTimeout(() => {
        document.getElementById("results-anchor")?.scrollIntoView({ behavior: "smooth" });
      }, 80);
    } catch (err: any) {
      if (activeScanIdRef.current === currentScanId) {
        console.error(err);
        const errMsg = formatUserFacingError(err, "scan");
        setError(errMsg);
        addToast("error", "Scan Notice", errMsg);
      }
    } finally {
      if (activeScanIdRef.current === currentScanId) {
        setIsLoading(false);
      }
      isScanningRef.current = false;
    }
  };

  // Run Forensic Detection
  const handleDetectText = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (isScanningRef.current || isLoading) return; // Prevent concurrent re-entry synchronously
    await executeScan(inputText);
  };

  // Reset workspace
  const handleStartNewScan = () => {
    activeScanIdRef.current++;
    isScanningRef.current = false;
    setIsLoading(false);
    setResult(null);
    setInputText("");
    setError(null);
    setActiveTab("paste");
    addToast("info", "New Scan Started", "Ready for your next document.");
    requestAnimationFrame(() => {
      setTimeout(() => {
        const inputCard = document.getElementById("detector-input-card");
        const textarea = document.getElementById("pasted-text-area");
        if (inputCard) {
          inputCard.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (textarea) {
          textarea.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        textarea?.focus();
      }, 70);
    });
  };

  const handleSwitchTab = (tab: "paste" | "ocr") => {
    setActiveTab(tab);
    setError(null);
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (tab === "paste") {
          const el = document.getElementById("detector-input-container") || document.getElementById("detector-input-card");
          const textarea = document.getElementById("pasted-text-area");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          textarea?.focus();
        } else if (tab === "ocr") {
          const el = document.getElementById("ocr-preprocessor-panel");
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 70);
    });
  };

  const handleBackToTextInput = () => {
    setActiveTab("paste");
    requestAnimationFrame(() => {
      setTimeout(() => {
        const inputCard = document.getElementById("detector-input-container") || document.getElementById("detector-input-card");
        const textarea = document.getElementById("pasted-text-area");
        if (inputCard) {
          inputCard.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (textarea) {
          textarea.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        textarea?.focus();
      }, 70);
    });
  };

  const handleOcrCompleted = (extractedText: string, engine?: "local" | "gemini") => {
    const cleanText = sanitizeUserInput(extractedText);
    setInputText(cleanText);
    setActiveTab("paste");
    setError(null);
    addToast("success", "OCR Extracted", `${countWords(cleanText)} words loaded into Document Detector (${engine === "gemini" ? "External AI" : "Local WASM"}).`);
    requestAnimationFrame(() => {
      setTimeout(() => {
        const inputCard = document.getElementById("detector-input-card");
        const textarea = document.getElementById("pasted-text-area");
        if (inputCard) {
          inputCard.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (textarea) {
          textarea.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        textarea?.focus();
      }, 80);
    });
  };

  // Process pasted image/screenshot directly into the detector text area via Gemini AI OCR
  const handleProcessPastedImageFile = async (file: File) => {
    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isImage && !isPdf) return false;

    // Daily Document Computing Power Check (Claude AI Style)
    const estimatedSize = file.size || 500 * 1024;
    const computeCheck = checkDailyComputeLimit(estimatedSize);
    if (!computeCheck.allowed) {
      addToast(
        "warning",
        "Daily Compute Power Limit Exceeded",
        computeCheck.reason || `Uploading this file exceeds your remaining daily document computing allowance (${computeCheck.formattedRemaining}). Refreshes tomorrow in ${computeCheck.resetCountdown}.`
      );
      return false;
    }

    setIsTranscribingPastedImage(true);
    setError(null);
    addToast("info", "Processing Screenshot", "Transcribing clipboard screenshot with External AI OCR for high efficiency...");

    try {
      const reader = new FileReader();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error("Failed to read pasted image data"));
        reader.readAsDataURL(file);
      });

      let payload = dataUrl;
      if (isImage) {
        try {
          const { optimizeImageForOcr } = await import("./lib/fastOcr");
          const optimized = await optimizeImageForOcr(dataUrl, 1400, 0.88);
          payload = optimized.dataUrl;
        } catch (e) {
          console.warn("Image optimization fallback:", e);
        }
      }

      let rawText = "";
      let isFallbackModel = false;

      try {
        const response = await fetch("/api/ocr-gemini", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Device-Id": getUserSessionId()
          },
          body: JSON.stringify({
            image: payload,
            filename: file.name || "Clipboard-Screenshot.png"
          })
        });

        const data = await response.json();
        if (response.ok && data.text) {
          rawText = data.text.trim();
          isFallbackModel = Boolean(data.isFallbackModel);
        } else if (isImage) {
          const { runFastLocalOcr } = await import("./lib/fastOcr");
          const localResult = await runFastLocalOcr(payload);
          rawText = localResult.text?.trim() || "";
        } else {
          throw new Error(data.error || "Failed to extract text from pasted image.");
        }
      } catch (geminiErr: any) {
        if (isImage) {
          const { runFastLocalOcr } = await import("./lib/fastOcr");
          const localResult = await runFastLocalOcr(payload);
          rawText = localResult.text?.trim() || "";
        } else {
          throw geminiErr;
        }
      }

      // Record consumed compute power
      const consumedBytes = file.size || estimateBase64SizeBytes(payload);
      recordDailyComputeUsage(consumedBytes, isPdf ? "pdf" : "screenshot");

      if (!rawText) {
        throw new Error("No readable text was found in the pasted screenshot.");
      }

      const injectionCheck = detectAndSanitizePromptInjection(rawText);
      const clean = sanitizeUserInput(injectionCheck.sanitizedText);
      setInputText(clean);
      setError(null);
      setResult(null);
      if (isFallbackModel) {
        addToast("info", "Screenshot Transcribed", `${countWords(clean)} words extracted via External AI fallback engine due to high demand.`);
      } else {
        addToast("success", "Screenshot Transcribed", `${countWords(clean)} words extracted into the detector.`);
      }
      return true;
    } catch (err: any) {
      console.error("Error transcribing pasted image:", err);
      const errMsg = formatUserFacingError(err, "transcription");
      setError(errMsg);
      addToast("error", "Transcription Notice", errMsg);
      return false;
    } finally {
      setIsTranscribingPastedImage(false);
    }
  };

  // Detector-specific paste event handler (catches screenshot/image blobs from Ctrl+V / Cmd+V)
  const handleDetectorPaste = (e: React.ClipboardEvent<HTMLTextAreaElement | HTMLDivElement>) => {
    const file = extractClipboardFile(e.clipboardData);
    if (file) {
      e.preventDefault();
      handleProcessPastedImageFile(file);
    }
  };

  // Window-level paste listener when in detector tab
  useEffect(() => {
    const handleGlobalPaste = (e: ClipboardEvent) => {
      // Only process when in detector (paste) tab and not currently inside other input elements
      if (activeTab !== "paste") return;
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || (target.tagName === "TEXTAREA" && target.id !== "pasted-text-area"))) {
        return;
      }

      const file = extractClipboardFile(e.clipboardData);
      if (file) {
        e.preventDefault();
        handleProcessPastedImageFile(file);
      }
    };

    window.addEventListener("paste", handleGlobalPaste);
    return () => {
      window.removeEventListener("paste", handleGlobalPaste);
    };
  }, [activeTab]);

  // History operations
  const handleSelectHistoryItem = (item: HistoryItem) => {
    activeScanIdRef.current++;
    isScanningRef.current = false;
    setIsLoading(false);
    let fullText = item.text;
    // Reconstruct full text for legacy items that were truncated to 80 chars
    if (fullText.endsWith("...") && item.result?.segments && item.result.segments.length > 0) {
      const reconstructed = item.result.segments.map(s => s.text).join(" ").trim();
      if (reconstructed.length > fullText.length) {
        fullText = reconstructed;
      }
    }
    setResult(item.result);
    setInputText(fullText);
    setActiveTab("paste");
    setIsHistoryDrawerOpen(false);
    addToast("info", "Scan Restored", `Restored scan from ${new Date(item.timestamp).toLocaleTimeString()}`);
    setTimeout(() => {
      document.getElementById("results-anchor")?.scrollIntoView({ behavior: "smooth" });
    }, 60);
  };

  const handleDeleteHistoryItem = (id: string) => {
    const deletedItem = history.find(item => item.id === id);
    const updated = history.filter(item => item.id !== id);
    saveHistory(updated);
    if (deletedItem) {
      addToast("info", "Item Deleted", "Scan removed from history.", 5000, {
        label: "Undo",
        onClick: () => {
          saveHistory([deletedItem, ...updated]);
          addToast("success", "Item Restored", "Scan restored to history.");
        }
      });
    } else {
      addToast("info", "Item Deleted", "Scan removed from history.");
    }
  };

  const handleClearAllHistory = () => {
    const previousHistory = [...history];
    saveHistory([]);
    addToast("info", "History Cleared", "All local scans have been purged.", 5000, {
      label: "Undo",
      onClick: () => {
        saveHistory(previousHistory);
        addToast("success", "History Restored", "All scans have been restored.");
      }
    });
  };

  const handleClearDocumentText = () => {
    activeScanIdRef.current++;
    isScanningRef.current = false;
    setIsLoading(false);
    if (!inputText && !result) return;
    setIsClearing(true);
    const prevText = inputText;
    const prevResult = result;
    setInputText("");
    setResult(null);
    setError(null);
    setActiveTab("paste");
    setTimeout(() => {
      setIsClearing(false);
    }, 250);
    addToast("info", "Document Cleared", "Text and results reset.", 5000, {
      label: "Undo",
      onClick: () => {
        setInputText(prevText);
        setResult(prevResult);
        setActiveTab("paste");
        addToast("success", "Document Restored", "Restored text and analysis report.");
        requestAnimationFrame(() => {
          setTimeout(() => {
            document.getElementById("results-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 80);
        });
      }
    });
    requestAnimationFrame(() => {
      setTimeout(() => {
        const inputCard = document.getElementById("detector-input-card");
        const textarea = document.getElementById("pasted-text-area");
        if (inputCard) {
          inputCard.scrollIntoView({ behavior: "smooth", block: "start" });
        } else if (textarea) {
          textarea.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        textarea?.focus();
      }, 70);
    });
  };

  const handleLoadSampleEssay = async (type: "academic" | "ai") => {
    activeScanIdRef.current++;
    isScanningRef.current = false;
    setIsLoading(false);
    setLoadingSample(type);
    setActiveTab("paste");
    setResult(null);

    try {
      // Retrieve the next dynamic sample on-demand from the training database
      const { getNextSampleFromDatabase } = await import("./lib/datasetStore");
      const sample = getNextSampleFromDatabase(type);
      setInputText(sample.text);
      setError(null);

      addToast(
        "info",
        type === "academic" ? `Sample #${sample.index}: Human Scholarship Loaded` : `Sample #${sample.index}: AI Text Loaded`,
        `Sample loaded into editor. Review, edit, or click 'Analyze Text' when ready.`
      );
    } catch {
      // Graceful fallback
    } finally {
      setLoadingSample(null);
    }

    requestAnimationFrame(() => {
      setTimeout(() => {
        const textarea = document.getElementById("pasted-text-area");
        textarea?.focus();
      }, 50);
    });
  };

  const handleFeedbackSaved = (feedback: UserFeedback | null) => {
    clearAnalysisCache();
    const recalibratedResult =
      inputText && countWords(inputText) >= 15
        ? analyzeTextLocally(inputText, weights, { lowConfidence, highConfidence, domainPreset })
        : result;

    if (recalibratedResult) {
      setResult(recalibratedResult);
    }

    const updated = history.map((item) => {
      if (item.text === inputText || item.text.slice(0, 80) === inputText.slice(0, 80)) {
        return {
          ...item,
          feedback: feedback || undefined,
          result: recalibratedResult || item.result
        };
      }
      return item;
    });
    saveHistory(updated);

    if (!feedback) {
      addToast("info", "Feedback Reset", "Removed custom verification override and restored baseline score.");
      return;
    }

    const labelTitle = feedback.actualLabel === "ai" 
      ? "AI-Generated" 
      : feedback.actualLabel === "human" 
        ? "Human-Written" 
        : feedback.actualLabel?.replace(/_/g, " ") || "verified";

    const shiftNote = recalibratedResult?.feedbackShiftApplied
      ? ` (${recalibratedResult.feedbackShiftApplied > 0 ? "+" : ""}${recalibratedResult.feedbackShiftApplied}% AI shift → ${recalibratedResult.overall_prediction} at ${recalibratedResult.confidence_score}%)`
      : "";

    addToast(
      "success",
      feedback.isCorrect ? "Prediction Verified & Calibrated" : "Calibrated & Added to Training Data",
      feedback.isCorrect 
        ? `Confirmed as ${labelTitle}${shiftNote}.`
        : `Calibrated as ${labelTitle}${shiftNote} and prioritized (75x weight) in training corpus.`
    );
  };

  const handleUpdateHistoryItemFeedback = (itemId: string, feedback: UserFeedback) => {
    clearAnalysisCache();
    const updated = history.map((item) => {
      if (item.id === itemId) {
        const recalibrated =
          item.text && countWords(item.text) >= 15
            ? analyzeTextLocally(item.text, weights, { lowConfidence, highConfidence, domainPreset })
            : item.result;
        if (item.text === inputText && recalibrated) {
          setResult(recalibrated);
        }
        return { ...item, feedback, result: recalibrated };
      }
      return item;
    });
    saveHistory(updated);
    addToast("success", "Feedback & Score Updated", "Scan verdict calibrated and saved to retraining archive.");
  };

  // Domain Preset Handler
  const handleDomainPresetChange = (preset: DomainPreset) => {
    setDomainPreset(preset);
    const newWeights = { ...DOMAIN_PRESET_WEIGHTS[preset] };
    setWeights(newWeights);
    safeSessionStorage.setItem("catchai_model_weights", JSON.stringify(newWeights));
    safeSessionStorage.setItem("veritext_model_weights", JSON.stringify(newWeights));
    addToast(
      "info",
      `Preset Applied: ${preset.toUpperCase()}`,
      `Calibrated burstiness and stylometric thresholds for ${preset} writing.`
    );
    if (result && inputText) {
      // Re-run analysis with newly calibrated preset
      const updatedRes = analyzeTextLocally(inputText, newWeights, { lowConfidence, highConfidence, domainPreset: preset });
      setResult(updatedRes);
    }
  };

  // Model calibration handlers
  const handleUpdateWeight = (key: string, value: number) => {
    const updated = { ...weights, [key]: value };
    setWeights(updated);
    safeSessionStorage.setItem("catchai_user_custom_weights", "true");
    safeSessionStorage.setItem("veritext_user_custom_weights", "true");
    safeSessionStorage.setItem("catchai_model_weights", JSON.stringify(updated));
    safeSessionStorage.setItem("veritext_model_weights", JSON.stringify(updated));
    if (result && inputText && countWords(inputText) >= 15) {
      clearAnalysisCache();
      const updatedRes = analyzeTextLocally(inputText, updated, { lowConfidence, highConfidence, domainPreset });
      setResult(updatedRes);
    }
  };

  const handleResetWeights = () => {
    safeSessionStorage.removeItem("catchai_user_custom_weights");
    safeSessionStorage.removeItem("veritext_user_custom_weights");
    fetch("/api/weights")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const targetWeights = data?.weights || DEFAULT_CLIENT_WEIGHTS;
        setWeights({ ...targetWeights });
        safeSessionStorage.setItem("catchai_model_weights", JSON.stringify(targetWeights));
        safeSessionStorage.setItem("veritext_model_weights", JSON.stringify(targetWeights));
        if (result && inputText && countWords(inputText) >= 15) {
          clearAnalysisCache();
          setResult(analyzeTextLocally(inputText, targetWeights, { lowConfidence, highConfidence, domainPreset }));
        }
      })
      .catch(() => {
        setWeights({ ...DEFAULT_CLIENT_WEIGHTS });
        safeSessionStorage.setItem("catchai_model_weights", JSON.stringify(DEFAULT_CLIENT_WEIGHTS));
        safeSessionStorage.setItem("veritext_model_weights", JSON.stringify(DEFAULT_CLIENT_WEIGHTS));
      });
    addToast("info", "Defaults Restored", "Calibration weights reset to standard baseline.");
  };

  const handleTrainModel = (customRows?: TrainingRow[]) => {
    setIsTraining(true);
    setTrainingSummary(null);

    setTimeout(async () => {
      try {
        const { trainModelWeightedLogistic } = await import("./lib/detectClient");
        const { getInitialActiveDataset, saveCustomDefaultWeights } = await import("./lib/datasetStore");
        const { getAllFlaggedAndVerifiedSamples, resolveFeedbackGroundTruth } = await import("./lib/feedbackStore");

        const baseRows: TrainingRow[] = (customRows && customRows.length > 0)
          ? customRows
          : getInitialActiveDataset().rows;

        // Guarantee all user-flagged samples carry 75x priority weight during training
        const flaggedSamples = getAllFlaggedAndVerifiedSamples();
        const flaggedNormMap = new Map<string, 0 | 1>();
        for (const fb of flaggedSamples) {
          const txt = (fb.fullText || fb.textSnippet || "").trim();
          if (txt.length >= 10) {
            const gt = resolveFeedbackGroundTruth(fb);
            if (gt) {
              flaggedNormMap.set(txt.toLowerCase().replace(/\s+/g, " "), gt === "ai" ? 1 : 0);
            }
          }
        }

        const rowsToTrain: TrainingRow[] = baseRows.map((r) => {
          const norm = (r.text || "").trim().toLowerCase().replace(/\s+/g, " ");
          if (r.isUserFlagged || flaggedNormMap.has(norm)) {
            return {
              ...r,
              label: flaggedNormMap.get(norm) ?? r.label,
              isUserFlagged: true,
              sampleWeight: r.sampleWeight ?? 75
            };
          }
          return r;
        });

        const trained = trainModelWeightedLogistic(rowsToTrain, weights, {
          lambda: 0.005,
          epochs: 2000,
          lr: 0.12
        });

        setWeights(trained.weights);
        saveCustomDefaultWeights(trained.weights);
        safeSessionStorage.setItem("catchai_model_weights", JSON.stringify(trained.weights));
        safeSessionStorage.setItem("veritext_model_weights", JSON.stringify(trained.weights));
        setTrainingSummary(trained.summary);

        // Clear client & server detection caches and immediately re-score active document if loaded
        clearAnalysisCache();
        fetch("/api/detect/clear-cache", { method: "POST" }).catch(() => {});

        if (inputText && countWords(inputText) >= 15) {
          const updatedRes = analyzeTextLocally(inputText, trained.weights, {
            lowConfidence,
            highConfidence,
            domainPreset
          });
          setResult(updatedRes);
        }

        const flaggedNote = trained.summary.flaggedCount
          ? ` (${trained.summary.flaggedCount} priority user-verified sample${trained.summary.flaggedCount === 1 ? "" : "s"} weighted at 75x)`
          : "";

        addToast(
          "success",
          "Calibration Complete & Applied",
          `Trained on ${trained.summary.sampleCount.toLocaleString()} samples${flaggedNote}. Optimal F1: ${(trained.summary.finalF1 * 100).toFixed(1)}% at threshold ${trained.summary.chosenThresholdPercent}%.`
        );
      } catch (err) {
        console.error("Training error:", err);
        addToast("error", "Training Failed", "An error occurred during local weight adjustment.");
      } finally {
        setIsTraining(false);
      }
    }, 150);
  };

  // Print & PDF
  const handlePrintReport = () => {
    addToast("info", "Opening Print Dialog", "Preparing print version of forensic report.");
    try {
      window.print();
    } catch (err: any) {
      console.warn("window.print() encountered an error or was restricted by iframe sandbox:", err);
      addToast("warning", "Print Blocked by Frame", "Direct print dialog is restricted in preview mode. Generating PDF certificate instead...");
      handleDownloadPdf();
    }
  };

  const handleDownloadPdf = async () => {
    const activeResult = effectiveResult || result;
    if (!activeResult) {
      addToast("warning", "No Active Scan", "Please analyze a document first to generate an audit PDF.");
      return;
    }
    setIsGeneratingPdf(true);
    addToast("info", "Generating Audit PDF", "Compiling forensic stylometrics into binary PDF certificate...");
    try {
      const { generatePdfReport } = await import("./lib/pdfExport");
      generatePdfReport(activeResult, inputText);
      addToast("success", "PDF Exported", "Official Forensic Audit Certificate report downloaded successfully.");
    } catch (err: any) {
      console.error("PDF export error:", err);
      addToast("error", "Export Failed", "Could not generate binary PDF. Falling back to print dialog.");
      window.print();
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Direct routing for the non-obvious Admin Authentication Portal (matches configured portal, /portal-*, etc.)
  const configuredPortal = typeof __ADMIN_PORTAL_PATH__ !== "undefined" ? __ADMIN_PORTAL_PATH__ : "/portal-7x92-access";
  const isPortalRoute =
    currentPath === configuredPortal ||
    currentPath.startsWith("/portal-") ||
    currentPath === "/portal-7x92-access" ||
    currentPath === "/portal-7x82-access" ||
    currentPath === "/thisisthesettings";

  if (isPortalRoute) {
    return (
      <Suspense fallback={
        <div className="min-h-screen flex flex-col items-center justify-center bg-slate-900 text-white font-mono text-sm gap-2">
          <div className="w-6 h-6 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
          <span>Loading admin authentication gateway...</span>
        </div>
      }>
        <AdminPortalLogin
          onLoginSuccess={() => {
            setIsAdminAuthenticated(true);
            window.history.pushState({}, "", "/");
            setCurrentPath("/");
            setIsCalibrationModalOpen(true);
            addToast("success", "Admin Access Granted", "Welcome to the administrative calibration panel.");
          }}
          onCancel={() => {
            window.history.pushState({}, "", "/");
            setCurrentPath("/");
          }}
        />
      </Suspense>
    );
  }

  // Dedicated Multi-Page View: Legal, Terms, Privacy, Ethics & Documentation Hub (Claude / Amazon / Stripe style)
  if (currentView === "legal") {
    return (
      <LegalHubPage
        initialSection={activeLegalSection}
        onNavigateHome={handleNavigateToHome}
        onNavigateSection={(sec) => {
          setActiveLegalSection(sec);
          try {
            window.history.pushState({ view: "legal", section: sec }, "", `/${sec}`);
          } catch {}
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex flex-col font-sans transition-colors duration-200 antialiased selection:bg-slate-900 selection:text-white dark:selection:bg-white dark:selection:text-slate-900 relative">
      
      {/* Global Header & Navigation */}
      <header 
        className={`bg-white/90 dark:bg-slate-900/90 backdrop-blur-md border-b border-slate-200/80 dark:border-slate-800/80 sticky top-0 left-0 right-0 z-40 w-full transition-transform duration-300 ease-in-out will-change-transform ${
          isNavVisible 
            ? "translate-y-0" 
            : "-translate-y-full pointer-events-none"
        }`} 
        id="global-navigation-header"
      >
        <div className="w-full max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-2 sm:gap-6 min-w-0">
          
          {/* Brand Signature with Pure SVG Logo */}
          <div 
            onClick={handleNavigateToHome}
            className="flex items-center gap-2 sm:gap-2.5 cursor-pointer select-none group min-w-0 shrink-0"
            title="CatchAI Text — Document Authenticity Verification"
            id="brand-logo-trigger"
          >
            <CatchAiLogo size={34} className="shrink-0" />
            <span className="font-display font-bold text-base sm:text-xl text-slate-900 dark:text-white tracking-tight leading-none truncate">
              CatchAI <span className="text-indigo-600 dark:text-indigo-400 font-extrabold">Text</span>
            </span>
          </div>

          {/* Centered Segmented Workspace Switcher (Desktop / Laptop: >= lg) */}
          <div className="hidden lg:flex items-center bg-slate-100 dark:bg-slate-800/90 p-1 rounded-xl border border-slate-200/70 dark:border-slate-700/70 shrink min-w-0 gap-1 shadow-2xs" id="segmented-workspace-tabs">
            <button
              type="button"
              onClick={() => handleSwitchTab("paste")}
              title="Single document analysis and sentence heatmaps (Laptop & Desktop)"
              className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap min-h-[34px] ${
                activeTab === "paste"
                  ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white font-semibold shadow-xs"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
              }`}
              id="tab-detector"
            >
              <FileText className={`w-3.5 h-3.5 shrink-0 ${activeTab === "paste" ? "text-slate-900 dark:text-white" : "text-slate-400"}`} />
              <span>Document Detector</span>
            </button>

            <button
              type="button"
              onClick={() => handleSwitchTab("ocr")}
              title="Upload documents or capture via camera"
              className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 cursor-pointer whitespace-nowrap min-h-[34px] ${
                activeTab === "ocr"
                  ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white font-semibold shadow-xs"
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
              }`}
              id="tab-ocr"
            >
              <ScanLine className={`w-3.5 h-3.5 shrink-0 ${activeTab === "ocr" ? "text-slate-900 dark:text-white" : "text-slate-400"}`} />
              <span>Scan &amp; Upload</span>
            </button>
          </div>

          {/* Direct Quick-Links for FAQ & Policies (Claude / Stripe header pattern) */}
          <div className="hidden md:flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                const el = document.getElementById("methodology-and-faq-panel");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
              title="Jump to Frequently Asked Questions"
            >
              FAQ
            </button>
            <button
              type="button"
              onClick={() => handleNavigateToLegal("terms")}
              className="px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors cursor-pointer"
              title="Open full Terms of Service & Privacy Policy center"
            >
              Terms &amp; Policies
            </button>
          </div>

          {/* Top-Right Streamlined Utility Cluster */}
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">

            {/* History Trigger Button (Icon on mobile, Full on tablet/laptop) */}
            <button
              type="button"
              onClick={() => setIsHistoryDrawerOpen(true)}
              className="p-2 sm:px-3 sm:py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-750 hover:border-slate-300 dark:hover:border-slate-600 text-xs font-medium flex items-center justify-center gap-1.5 text-slate-700 dark:text-slate-200 transition-colors cursor-pointer h-9 shrink-0 active:scale-98"
              title={`Audit Log & Scan History (${history.length} scans saved)`}
              aria-label={`Audit Log & Scan History (${history.length} scans)`}
              id="audit-log-drawer-btn"
            >
              <History className="w-4 h-4 text-slate-500 dark:text-slate-400 shrink-0" />
              <span className="hidden sm:inline">History</span>
              {history.length > 0 && (
                <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                  {history.length}
                </span>
              )}
            </button>

            {/* Share App Trigger (Icon on mobile, Full on tablet/laptop) */}
            <button
              type="button"
              onClick={handleShareApp}
              className="p-2 sm:px-3 sm:py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-750 hover:border-slate-300 dark:hover:border-slate-600 text-xs font-medium flex items-center justify-center gap-1.5 text-slate-700 dark:text-slate-200 transition-colors cursor-pointer h-9 shrink-0 active:scale-98"
              title={`Share website address (${getWebsiteAddress()})`}
              aria-label="Share website address"
              id="header-share-app-btn"
            >
              {isShareCopied ? (
                <>
                  <Check className="w-4 h-4 text-emerald-500" />
                  <span className="hidden sm:inline text-emerald-600 dark:text-emerald-400 font-semibold">Copied!</span>
                </>
              ) : (
                <>
                  <Share2 className="w-4 h-4 text-slate-500 dark:text-slate-400 shrink-0" />
                  <span className="hidden sm:inline">Share</span>
                </>
              )}
            </button>

            {/* Calibration Trigger (Discreetly visible ONLY for authenticated administrators) */}
            {isAdminAuthenticated && (
              <button
                type="button"
                onClick={() => setIsCalibrationModalOpen(true)}
                className="p-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-xs font-medium flex items-center justify-center text-slate-700 dark:text-slate-300 transition-colors cursor-pointer h-9 w-9 shrink-0 active:scale-98"
                title="Admin Model Calibration & Settings (Active Session)"
                aria-label="Admin Model Calibration & Settings"
                id="model-calibration-btn"
              >
                <Sliders className="w-4 h-4" />
              </button>
            )}

            {/* Theme Selector Dropdown */}
            <ThemeSelector
              themeMode={themeMode}
              onThemeChange={(newMode) => {
                setThemeMode(newMode);
              }}
            />

          </div>
        </div>

        {/* Mobile & Tablet Tab Switcher Bar (visible < lg) - Touch Target Compliant >= 44px */}
        <div className="flex lg:hidden border-t border-slate-200/80 dark:border-slate-800/80 px-2 sm:px-3 py-1.5 bg-slate-50/95 dark:bg-slate-900/95 backdrop-blur-md">
          <div className="grid grid-cols-2 gap-1 p-1 bg-slate-200/70 dark:bg-slate-800/80 rounded-xl border border-slate-300/50 dark:border-slate-700/60 w-full max-w-xl mx-auto" role="tablist" aria-label="Workspace view switch">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "paste"}
              onClick={() => handleSwitchTab("paste")}
              className={`py-2 px-2 text-center rounded-lg flex items-center justify-center gap-1.5 transition-all cursor-pointer min-h-[44px] ${
                activeTab === "paste" 
                  ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-xs font-semibold" 
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white font-medium"
              }`}
              id="mobile-tab-detector"
            >
              <FileText className={`w-4 h-4 shrink-0 ${activeTab === "paste" ? "text-slate-900 dark:text-white" : "text-slate-400"}`} />
              <span className="text-xs sm:text-sm font-medium leading-tight truncate">Document Detector</span>
            </button>

            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "ocr"}
              onClick={() => handleSwitchTab("ocr")}
              className={`py-2 px-2 text-center rounded-lg flex items-center justify-center gap-1.5 transition-all cursor-pointer min-h-[44px] ${
                activeTab === "ocr" 
                  ? "bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-xs font-semibold" 
                  : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white font-medium"
              }`}
              id="mobile-tab-ocr"
            >
              <ScanLine className={`w-4 h-4 shrink-0 ${activeTab === "ocr" ? "text-slate-900 dark:text-white" : "text-slate-400"}`} />
              <span className="text-xs sm:text-sm font-medium leading-tight truncate">Scan &amp; Upload</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Workspace Area */}
      <main className="max-w-7xl mx-auto w-full px-3 sm:px-4 md:px-6 lg:px-8 py-3.5 sm:py-6 flex-1 flex flex-col gap-4 sm:gap-6 relative z-10 min-w-0 laptop-viewport-balanced pb-safe">
        
        {/* Offline Banner */}
        {isOffline && (
          <div className="bg-amber-500/10 text-amber-800 dark:text-amber-200 text-xs px-4 py-2.5 rounded-2xl border border-amber-500/30 flex items-center justify-between gap-3 font-medium backdrop-blur-md shadow-xs">
            <div className="flex items-center gap-2">
              <WifiOff className="w-4 h-4 text-amber-500 animate-pulse" />
              <span>Offline Mode Active &bull; CatchAI Text client-side engine running locally with zero latency.</span>
            </div>
            <span className="text-[10px] font-mono uppercase bg-amber-500/20 px-2 py-0.5 rounded-md font-bold">100% Local</span>
          </div>
        )}

        {/* Dynamic Workspace Tab Content (Persistent across tab switches to preserve in-progress PDF uploads and workspace states) */}
        <div className="tab-contents-wrapper w-full">
          {/* Document Detector Tab */}
          <div
            id="panel-detector-view"
            role="tabpanel"
            aria-labelledby="tab-detector"
            className={activeTab === "paste" ? "flex flex-col gap-6" : "hidden"}
          >
            {/* Document Input Box & Action Stage */}
            <TextEditorPanel
              inputText={inputText}
              setInputText={setInputText}
              handleDetectText={handleDetectText}
              isLoading={isLoading}
              error={error}
              setError={setError}
              domainPreset={domainPreset}
              handleDomainPresetChange={handleDomainPresetChange}
              handleClearDocumentText={handleClearDocumentText}
              isClearing={isClearing}
              handleDetectorPaste={handleDetectorPaste}
              isTranscribingPastedImage={isTranscribingPastedImage}
              wordCount={wordCount}
              loadingSample={loadingSample}
              handleLoadSampleEssay={handleLoadSampleEssay}
              estimateRealtimeHumanity={estimateRealtimeHumanity}
              onAddToast={addToast}
              onOpenScoreGuide={() => setIsScoreGuideOpen(true)}
            />

            {/* Anchor for Results */}
            <div id="results-anchor" className="scroll-mt-20" />

            {/* Skeleton Loader & Animated Results Component with Subtle Fade-in and Slide-up */}
            <AnimatePresence mode="wait">
              {isLoading ? (
                <motion.div
                  key="results-skeleton-loader"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ResultSkeletonLoader text={inputText} />
                </motion.div>
              ) : result ? (
                <Suspense fallback={<ResultSkeletonLoader text={inputText} />}>
                  <DetectionResultsSection
                    key={`results-root-${result.timestamp || activeScanIdRef.current || "completed"}`}
                    result={effectiveResult || result}
                    inputText={inputText}
                    theme={theme}
                    lowConfidence={lowConfidence}
                    highConfidence={highConfidence}
                    calibrationBias={weights.calibrationBias}
                    isGeneratingPdf={isGeneratingPdf}
                    onDownloadPdf={handleDownloadPdf}
                    onPrintReport={handlePrintReport}
                    onStartNewScan={handleStartNewScan}
                    onBackToTextInput={handleBackToTextInput}
                    onFeedbackSaved={handleFeedbackSaved}
                    onOpenScoreGuide={() => setIsScoreGuideOpen(true)}
                    onAddToast={addToast}
                  />
                </Suspense>
              ) : null}
            </AnimatePresence>
          </div>

          {/* OCR Studio Workspace Tab */}
          <div
            id="panel-ocr-view"
            role="tabpanel"
            aria-labelledby="tab-ocr"
            className={activeTab === "ocr" ? "block" : "hidden"}
          >
            <Suspense fallback={<OcrSkeletonLoader />}>
              <OcrPreprocessor
                onTextExtracted={handleOcrCompleted}
                isLoading={ocrLoading}
                setIsLoading={setOcrLoading}
                onError={(title, msg) => addToast("error", title, formatUserFacingError(msg), 5000)}
                onWarning={(title, msg) => addToast("warning", title, formatUserFacingError(msg), 5000)}
                isActive={activeTab === "ocr"}
              />
            </Suspense>
          </div>
        </div>

        {/* Consolidated Academic Documentation, Interactive Case Studies & FAQ Hub */}
        <AcademicInfoSection
          onLoadSampleIntoEditor={(sampleText, preset) => {
            handleSwitchTab("paste");
            if (preset) setDomainPreset(preset);
            setInputText(sampleText);
            setResult(null);
            window.scrollTo({ top: 0, behavior: "smooth" });
            addToast("info", "Benchmark Sample Loaded", "Click 'Analyze Document' in the editor to inspect the stylometric breakdown.");
          }}
          onNavigateToLegal={handleNavigateToLegal}
        />

      </main>

      {/* Academic & Legal Compliance Standard Website Footer (Reference: Claude, Amazon, Stripe) */}
      <footer className="bg-white dark:bg-slate-950 border-t border-slate-200/90 dark:border-slate-850 mt-16 transition-colors" id="app-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12">
            
            {/* Column 1 & 2: Brand Identity & Institutional Affiliation */}
            <div className="lg:col-span-2 flex flex-col gap-4">
              <div className="flex items-center gap-2.5">
                <CatchAiLogo size={32} className="shrink-0" />
                <div className="flex items-baseline gap-2">
                  <span className="font-display font-bold text-lg text-slate-900 dark:text-white tracking-tight">
                    CatchAI <span className="text-indigo-600 dark:text-indigo-400 font-extrabold">Text</span>
                  </span>
                  <span className="text-[11px] font-mono text-slate-500 dark:text-slate-400">
                    v3.2
                  </span>
                </div>
              </div>

              <p className="text-sm text-slate-600 dark:text-slate-400 max-w-sm leading-relaxed">
                Open-access AI writing forensics and document authenticity verification platform. Built with transparent stylometric metrics, on-device WebAssembly, and Explainable AI (XAI).
              </p>

              <div className="pt-2 text-xs text-slate-500 dark:text-slate-400 flex flex-col gap-1 border-t border-slate-100 dark:border-slate-850">
                <span className="font-semibold text-slate-800 dark:text-slate-200">
                  Petroleum Training Institute (PTI)
                </span>
                <span>Department of Computer Engineering Technology</span>
                <span>P.M.B. 20, Effurun, Delta State, Nigeria</span>
              </div>
            </div>

            {/* Column 3: Platform */}
            <div className="flex flex-col gap-3.5">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-900 dark:text-white">
                Platform
              </h4>
              <ul className="flex flex-col gap-2.5 text-sm text-slate-600 dark:text-slate-400">
                <li>
                  <a
                    href="/"
                    onClick={(e) => {
                      e.preventDefault();
                      handleSwitchTab("paste");
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className="hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer text-left"
                  >
                    Document Review
                  </a>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      handleSwitchTab("ocr");
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className="hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer text-left"
                  >
                    OCR Document Scanner
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => setIsHistoryDrawerOpen(true)}
                    className="hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer text-left"
                  >
                    Local Scan History
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => setIsCalibrationModalOpen(true)}
                    className="hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer text-left"
                  >
                    Model Calibration &amp; Weights
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={handleShareApp}
                    className="hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer text-left flex items-center gap-1.5"
                    title={`Share website address (${getWebsiteAddress()})`}
                  >
                    <span>Share App</span>
                    <Share2 className="w-3 h-3 text-slate-400" />
                  </button>
                </li>
              </ul>
            </div>

            {/* Column 4: Documentation & Support */}
            <div className="flex flex-col gap-3.5">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-900 dark:text-white">
                Support &amp; Help
              </h4>
              <ul className="flex flex-col gap-2.5 text-sm text-slate-600 dark:text-slate-400">
                <li>
                  <a
                    href="mailto:catchaitext@gmail.com"
                    className="hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer text-left flex items-center gap-1.5 font-medium text-slate-800 dark:text-slate-200"
                    title="Email CatchAI Text Support Desk"
                  >
                    <Mail className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                    <span>catchaitext@gmail.com</span>
                  </a>
                </li>
                <li>
                  <a
                    href="#frequently-asked-questions"
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.getElementById("frequently-asked-questions") || document.getElementById("methodology-and-faq-panel");
                      if (el) el.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer text-left"
                  >
                    Frequently Asked Questions
                  </a>
                </li>
                <li>
                  <a
                    href="/score-guide"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigateToLegal("score-guide");
                    }}
                    className="hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer text-left font-medium text-indigo-600 dark:text-indigo-400"
                  >
                    How to Interpret Scores
                  </a>
                </li>
                <li>
                  <a
                    href="/about"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigateToLegal("about");
                    }}
                    className="hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer text-left"
                  >
                    Stylometric Methodology
                  </a>
                </li>
                <li>
                  <a
                    href="/ethics"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigateToLegal("ethics");
                    }}
                    className="hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer text-left"
                  >
                    AI Ethics &amp; Bias Mitigation
                  </a>
                </li>
              </ul>
            </div>

            {/* Column 5: Legal & Policies (Crawlable semantic anchor links to dedicated pages) */}
            <div className="flex flex-col gap-3.5">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-900 dark:text-white">
                Legal &amp; Policies
              </h4>
              <ul className="flex flex-col gap-2.5 text-sm text-slate-600 dark:text-slate-400">
                <li>
                  <a
                    href="/terms"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigateToLegal("terms");
                    }}
                    className="hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer text-left"
                  >
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a
                    href="/privacy"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigateToLegal("privacy");
                    }}
                    className="hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer text-left"
                  >
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a
                    href="/ethics"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigateToLegal("ethics");
                    }}
                    className="hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer text-left"
                  >
                    EU AI Act Compliance
                  </a>
                </li>
                <li>
                  <a
                    href="/dmca"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigateToLegal("dmca");
                    }}
                    className="hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer text-left"
                  >
                    DMCA &amp; Copyright Policy
                  </a>
                </li>
                <li>
                  <a
                    href="/about"
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavigateToLegal("about");
                    }}
                    className="hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer text-left"
                  >
                    About CatchAI Text &amp; Methodology
                  </a>
                </li>
              </ul>
            </div>

          </div>

          {/* Bottom Divider & Baseline Copyright */}
          <div className="mt-12 pt-8 border-t border-slate-200/80 dark:border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500 dark:text-slate-400">
            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-3 gap-y-1">
              <span>&copy; {new Date().getFullYear()} CatchAI Text.</span>
              <span className="hidden sm:inline">&bull;</span>
              <a
                href="/terms"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigateToLegal("terms");
                }}
                className="hover:underline cursor-pointer"
              >
                Terms
              </a>
              <span>&bull;</span>
              <a
                href="/privacy"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigateToLegal("privacy");
                }}
                className="hover:underline cursor-pointer"
              >
                Privacy
              </a>
              <span>&bull;</span>
              <a
                href="/ethics"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigateToLegal("ethics");
                }}
                className="hover:underline cursor-pointer"
              >
                Ethics
              </a>
              <span>&bull;</span>
              <a
                href="/about"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigateToLegal("about");
                }}
                className="hover:underline cursor-pointer"
              >
                About
              </a>
              <span>&bull;</span>
              <a
                href="/score-guide"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigateToLegal("score-guide");
                }}
                className="hover:underline cursor-pointer"
              >
                Score Guide
              </a>
              <span>&bull;</span>
              <a
                href="/dmca"
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigateToLegal("dmca");
                }}
                className="hover:underline cursor-pointer"
              >
                DMCA
              </a>
              <span>&bull;</span>
              <a
                href="mailto:catchaitext@gmail.com"
                className="hover:underline cursor-pointer font-medium text-slate-700 dark:text-slate-300"
                title="Contact Support"
              >
                Support
              </a>
              <span className="hidden md:inline">&bull;</span>
              <span className="text-slate-600 dark:text-slate-400">Diagnostic indicators to support human review.</span>
            </div>

            <div className="flex items-center gap-4 shrink-0">
              <button
                type="button"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-750 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer"
                title="Scroll back to top"
                aria-label="Back to top"
              >
                <ArrowUp className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* History Slide-Out Drawer / Modal */}
      {isHistoryDrawerOpen && (
        <div 
          className="fixed inset-0 z-50 flex justify-end bg-slate-950/60 backdrop-blur-xs animate-fade-in cursor-pointer"
          onClick={(e) => {
            if (e.target === e.currentTarget) setIsHistoryDrawerOpen(false);
          }}
          role="dialog"
          aria-modal="true"
        >
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 250 }}
            className="w-full max-w-md bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 h-full p-4 sm:p-6 overflow-y-auto shadow-2xl flex flex-col gap-4 cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                <h3 className="font-display font-bold text-sm sm:text-base text-slate-900 dark:text-white">
                  Local Scan History
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsHistoryDrawerOpen(false)}
                title="Close (or click outside or press Esc)"
                className="p-1 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-white cursor-pointer transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <Suspense fallback={<HistorySkeletonLoader />}>
              <HistoryPanel
                history={history}
                onSelectHistoryItem={handleSelectHistoryItem}
                onDeleteHistoryItem={handleDeleteHistoryItem}
                onClearHistory={handleClearAllHistory}
                onUpdateHistoryItemFeedback={handleUpdateHistoryItemFeedback}
              />
            </Suspense>
          </motion.div>
        </div>
      )}

      {/* Model Calibration & Admin Studio Modal */}
      {isCalibrationModalOpen && (
        <Suspense fallback={null}>
          <ModelCalibrationModal
            isOpen={isCalibrationModalOpen}
            onClose={handleCloseCalibrationModal}
            isAdminAuthenticated={isAdminAuthenticated}
            weights={weights}
            onUpdateWeight={handleUpdateWeight}
            onResetWeights={handleResetWeights}
            onTrainModel={handleTrainModel}
            isTraining={isTraining}
            trainingSummary={trainingSummary}
            onNavigateToPortal={() => {
              handleCloseCalibrationModal();
              const portalPath = typeof __ADMIN_PORTAL_PATH__ !== "undefined" ? __ADMIN_PORTAL_PATH__ : "/portal-7x92-access";
              window.history.pushState({}, "", portalPath);
              setCurrentPath(portalPath);
            }}
            onAdminLogout={() => {
              setIsAdminAuthenticated(false);
              handleCloseCalibrationModal();
              addToast("info", "Session Terminated", "Administrative session has been safely closed.");
            }}
          />
        </Suspense>
      )}

      {/* Accessible Global Toast Feedback */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} onDismissAll={dismissAllToasts} />

      {/* Global Window Drag & Drop Overlay */}
      <AnimatePresence>
        {isWindowDragging && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-60 bg-slate-950/75 backdrop-blur-sm flex flex-col items-center justify-center p-6 pointer-events-none"
          >
            <div className="p-8 sm:p-12 rounded-3xl border-3 border-dashed border-slate-400 dark:border-slate-600 bg-white/95 dark:bg-slate-900/95 text-center flex flex-col items-center gap-4 max-w-md shadow-2xl animate-pulse">
              <div className="p-4 rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white">
                <Upload className="w-10 h-10" />
              </div>
              <h3 className="font-display font-bold text-lg sm:text-xl text-slate-900 dark:text-white">
                Drop Document Anywhere
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 font-medium">
                Release your image, PDF, DOCX, or text file anywhere to extract text and analyze stylometric authenticity.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* How to Interpret Forensic Scores Guide Modal */}
      {isScoreGuideOpen && (
        <Suspense fallback={null}>
          <ScoreInterpretationModal
            isOpen={isScoreGuideOpen}
            onClose={() => setIsScoreGuideOpen(false)}
            initialScore={result ? Math.round(result.confidence_score) : null}
          />
        </Suspense>
      )}

      {/* Privacy & Cookie Consent Banner */}
      <CookieBanner onOpenPrivacyPolicy={handleOpenPrivacyPolicy} />

    </div>
  );
}
